/**
 * ShiftPopover – Preset & custom shift controls for tasks
 */

import { useState, useRef, useEffect, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import {
  SHIFT_PRESETS,
  popoverStyle,
  presetBtnStyle,
  sectionLabelStyle,
  sectionDividerStyle,
} from './toolbarStyles';

export function ShiftPopover({
  onShift,
  onMoveToDate,
  onClose,
  hasSelection,
  triggerRef,
}: {
  onShift: (delta: number) => void;
  onMoveToDate: (date: string) => void;
  onClose: () => void;
  hasSelection: boolean;
  triggerRef?: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [customDays, setCustomDays] = useState('');
  const [targetDate, setTargetDate] = useState('');

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (triggerRef?.current?.contains(e.target as Node)) return;
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, triggerRef]);

  const target = hasSelection ? t('board.shiftSelected') : t('common.all');

  const handleCustomShift = (direction: 1 | -1) => {
    const days = parseInt(customDays, 10);
    if (days > 0) onShift(direction * days);
  };

  const handleDateMove = () => {
    if (targetDate) onMoveToDate(targetDate);
  };

  return (
    <div ref={ref} style={popoverStyle}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8, fontWeight: 600 }}>
        {target} {t('board.shiftTasks')}
      </div>

      <div style={sectionLabelStyle}>{t('board.shiftPresets')}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
        {SHIFT_PRESETS.map((p) => (
          <div key={p.label + p.days} style={{ display: 'flex', gap: 2 }}>
            <button
              style={presetBtnStyle}
              onClick={() => onShift(-p.days)}
              title={`${target} Tasks ${p.days} ${t('board.shiftLeft')}`}
            >
              <ChevronLeft size={10} />
              {p.label}
            </button>
            <button
              style={presetBtnStyle}
              onClick={() => onShift(p.days)}
              title={`${target} Tasks ${p.days} ${t('board.shiftRight')}`}
            >
              {p.label}
              <ChevronRight size={10} />
            </button>
          </div>
        ))}
      </div>

      <div style={sectionDividerStyle}>
        <div style={sectionLabelStyle}>{t('board.customDays')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            style={{ ...presetBtnStyle, flex: 'none', width: 28 }}
            onClick={() => handleCustomShift(-1)}
            title={`${target} ${t('board.shiftLeft')}`}
          >
            <ChevronLeft size={10} />
          </button>
          <input
            type="number"
            min="1"
            value={customDays}
            onChange={(e) => setCustomDays(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCustomShift(1);
            }}
            placeholder={t('board.daysPlaceholder')}
            style={{
              flex: 1,
              height: 28,
              fontSize: 11,
              border: '1px solid #e5e7eb',
              borderRadius: 5,
              padding: '0 6px',
              textAlign: 'center',
              outline: 'none',
              backgroundColor: '#fff',
              color: '#374151',
            }}
          />
          <button
            style={{ ...presetBtnStyle, flex: 'none', width: 28 }}
            onClick={() => handleCustomShift(1)}
            title={`${target} ${t('board.shiftRight')}`}
          >
            <ChevronRight size={10} />
          </button>
        </div>
      </div>

      <div style={sectionDividerStyle}>
        <div style={sectionLabelStyle}>{t('board.setStartDate')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleDateMove();
            }}
            style={{
              flex: 1,
              height: 28,
              fontSize: 11,
              border: '1px solid #e5e7eb',
              borderRadius: 5,
              padding: '0 6px',
              outline: 'none',
              backgroundColor: '#fff',
              color: targetDate ? '#374151' : '#9ca3af',
            }}
          />
          <button
            style={{ ...presetBtnStyle, flex: 'none', padding: '0 8px' }}
            onClick={handleDateMove}
            title={t('board.moveToDateTitle', { target })}
          >
            <Calendar size={10} />
            {t('board.moveButton')}
          </button>
        </div>
        <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>
          {t('board.moveHint')}
        </div>
      </div>
    </div>
  );
}
